import { useEffect, useState } from "react";

import { UseFormReturnType } from "@mantine/form";

import CumulativeBloodLossField, {
  BloodLossFormValues,
} from "@/encounters/components/EncounterLabor/QuestionnaireCumulativeBloodLossField";
import { IntakeFormComponentProps } from "@/intake-forms/types/intake-forms";
import {
  getFormControlCodeableConcept,
  getFormControlType,
} from "@/intake-forms/utils/intake-forms";
import { GenericFormValues } from "@/utils/questionnaires";

import QuestionnaireNumberInputField from "../QuestionnaireNumberInputField";
import TimerInput from "../QuestionnaireTimerInput";

export interface QuestionnaireDecimalFieldProps<T>
  extends Omit<IntakeFormComponentProps<T>, "key"> {
  error: string | undefined | React.ReactNode;
  withAsterisk?: boolean;
  cumulativeValue?: number;
  value?: number;
  fieldPath?: string;
  allowNegative?: boolean;
  maxDecimalPlaces?: number;
  showPlusPrefix?: boolean;
  "data-testid"?: string;
}

enum FormControlTypes {
  CumulativeBloodLoss = "cumulative-blood-loss",
  Timer = "timer",
}

export default function QuestionnaireDecimalField<T extends GenericFormValues>({
  form,
  formFieldName,
  name,
  label,
  required,
  placeholder,
  rightSection,
  minRange,
  maxRange,
  minValue,
  maxValue,
  maxDecimalPlaces,
  allowDecimal,
  allowNegative,
  showPlusPrefix,
  questionnaireItem,
  "data-testid": dataTestId,
}: QuestionnaireDecimalFieldProps<T>) {
  const [value, setValue] = useState<number | undefined>(undefined);
  const [fieldPath, setFieldPath] = useState<string>("");
  const formControlCodeableConcept = getFormControlCodeableConcept(questionnaireItem);
  const formControlType = getFormControlType(formControlCodeableConcept);

  useEffect(() => {
    const isInHorizontalGroup = formFieldName.includes(".");
    const computedFieldPath = isInHorizontalGroup ? formFieldName : name;
    setFieldPath(computedFieldPath);

    const currentValue = form.getInputProps(computedFieldPath).value;
    setValue(currentValue === "" ? undefined : Number(currentValue));
  }, [formControlCodeableConcept, formFieldName, name, form]);

  if (formControlType === FormControlTypes.CumulativeBloodLoss) {
    return (
      <CumulativeBloodLossField
        name={name}
        label={label}
        value={value}
        minRange={minRange}
        maxRange={maxRange}
        minValue={minValue}
        maxValue={maxValue}
        fieldPath={fieldPath}
        withAsterisk={required}
        placeholder={placeholder}
        rightSection={rightSection}
        allowDecimal={allowDecimal}
        formFieldName={formFieldName}
        allowNegative={allowNegative}
        error={form.errors[formFieldName]}
        maxDecimalPlaces={maxDecimalPlaces}
        questionnaireItem={questionnaireItem}
        data-testid="decimal-field-cumulative-blood-loss"
        form={form as unknown as UseFormReturnType<BloodLossFormValues>}
      />
    );
  }

  // Fallback: if the item is LOINC 9110-8 (Cumulative Blood Loss), render the specialized field
  const isCumulativeBloodLossLoinc = questionnaireItem?.code?.some(
    (c) => c.system === "http://loinc.org" && c.code === "9110-8",
  );
  if (isCumulativeBloodLossLoinc) {
    return (
      <CumulativeBloodLossField
        name={name}
        label={label}
        value={value}
        minRange={minRange}
        maxRange={maxRange}
        minValue={minValue}
        maxValue={maxValue}
        fieldPath={fieldPath}
        withAsterisk={required}
        placeholder={placeholder}
        rightSection={rightSection}
        allowDecimal={allowDecimal}
        formFieldName={formFieldName}
        allowNegative={allowNegative}
        error={form.errors[formFieldName]}
        maxDecimalPlaces={maxDecimalPlaces}
        questionnaireItem={questionnaireItem}
        data-testid="decimal-field-cumulative-blood-loss"
        form={form as unknown as UseFormReturnType<BloodLossFormValues>}
      />
    );
  }

  if (formControlType === FormControlTypes.Timer) {
    if (!questionnaireItem) return null;
    const isInHorizontalGroup = formFieldName.includes(".");
    const timerFieldPath = isInHorizontalGroup ? formFieldName : name;
    return <TimerInput form={form} label={label} formFieldPath={timerFieldPath} />;
  }

  return (
    <QuestionnaireNumberInputField<T>
      name={name}
      form={form}
      label={label}
      value={value}
      minRange={minRange}
      maxRange={maxRange}
      minValue={minValue}
      maxValue={maxValue}
      fieldPath={fieldPath}
      withAsterisk={required}
      data-testid={dataTestId}
      placeholder={placeholder}
      rightSection={rightSection}
      allowDecimal={allowDecimal}
      formFieldName={formFieldName}
      allowNegative={allowNegative}
      showPlusPrefix={showPlusPrefix}
      error={form.errors[formFieldName]}
      maxDecimalPlaces={maxDecimalPlaces}
      questionnaireItem={questionnaireItem}
      {...form.getInputProps(formFieldName)}
    />
  );
}
